import type { LoaderFunctionArgs, MetaFunction } from '@remix-run/node'
import { json } from '@remix-run/node'
import { Link, useLoaderData } from '@remix-run/react'
import invariant from 'tiny-invariant'

import { MarketingFooter } from '~/components/marketing/MarketingFooter'
import {
  allLegalPages,
  findLegalPage,
  type LegalPage,
} from '~/components/marketing/templates'

interface LoaderData {
  page: LegalPage
  /** Null for company-wide pages (e.g. the privacy policy) not owned by a
   *  single template. */
  templateTitle: string | null
  templateSlug: string | null
}

export async function loader({ params }: LoaderFunctionArgs) {
  invariant(params.page, 'page slug required')
  const entry = findLegalPage(params.page)
  if (!entry) {
    throw new Response('Not Found', { status: 404 })
  }
  return json<LoaderData>({
    page: entry.page,
    templateTitle: entry.template?.title ?? null,
    templateSlug: entry.template?.slug ?? null,
  })
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  if (!data) return [{ title: 'Not found — Quiver Labs' }]
  return [
    { title: `${data.page.title} — Quiver Labs` },
    {
      name: 'description',
      content:
        data.page.description ??
        (data.templateTitle
          ? `${data.page.title} for the ${data.templateTitle} template.`
          : `${data.page.title} for Quiver Labs.`),
    },
    { name: 'robots', content: 'index,follow' },
  ]
}

/**
 * Render a single block (paragraph OR bullet list) of markdown-ish content.
 * Mirrors the renderer used inside the in-product Terms accept page so the
 * public legal page and the in-product Terms doc render identically.
 */
function RenderBlock({ raw }: { raw: string }) {
  const lines = raw.split('\n')
  const allBullets =
    lines.length > 0 && lines.every((l) => /^\s*[-*]\s+/.test(l))
  if (allBullets) {
    return (
      <ul className="my-3 list-disc space-y-1 pl-6">
        {lines.map((l, i) => (
          <li
            key={i}
            dangerouslySetInnerHTML={{
              __html: inline(l.replace(/^\s*[-*]\s+/, '')),
            }}
          />
        ))}
      </ul>
    )
  }
  return (
    <p
      className="my-3 leading-relaxed"
      dangerouslySetInnerHTML={{ __html: inline(raw) }}
    />
  )
}

/** `**bold**` → `<strong>bold</strong>`. Source strings are author-controlled
 *  (in templates.ts), so dangerouslySetInnerHTML is safe — no user input
 *  ever flows into these blocks. */
function inline(s: string): string {
  return s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
}

function Body({ body }: { body: string }) {
  const blocks = body.trim().split(/\n\s*\n/)
  return (
    <>
      {blocks.map((b, i) => (
        <RenderBlock key={i} raw={b} />
      ))}
    </>
  )
}

export default function LegalPageRoute() {
  const { page, templateTitle, templateSlug } = useLoaderData<typeof loader>()
  return (
    <div className="flex min-h-screen flex-col bg-[#0b0c0e] text-zinc-100 antialiased">
      <div aria-hidden="true" className="fixed inset-0 -z-10 bg-[#0b0c0e]" />
      <main className="mx-auto w-full max-w-3xl flex-1 px-6 py-12 text-zinc-300">
        <Link
          to="/"
          className="text-xs uppercase tracking-wider text-zinc-500 transition-colors hover:text-zinc-300"
        >
          ← quiveros.ai
        </Link>

        <h1 className="mt-4 text-2xl font-semibold text-white">
          {page.title}
        </h1>
        <p className="mt-2 text-sm text-zinc-500">
          {page.description ??
            (templateTitle
              ? `${page.title} for the ${templateTitle} template.`
              : `${page.title} for Quiver Labs.`)}{' '}
          Document version <span className="font-mono">{page.version}</span>.
          {templateSlug && templateTitle ? (
            <>
              {' '}
              Template:{' '}
              <Link
                to={`/templates/${templateSlug}`}
                className="text-indigo-300 hover:underline"
              >
                {templateTitle}
              </Link>
              .
            </>
          ) : null}
        </p>

        <nav className="mt-6 rounded-lg border border-white/10 bg-white/[0.02] p-4 text-sm">
          <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
            Contents
          </p>
          <ul className="mt-2 space-y-1">
            {page.sections.map((s) => (
              <li key={s.id}>
                <a
                  href={`#${s.id}`}
                  className="text-indigo-300 hover:underline"
                >
                  {s.title}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className="mt-8 space-y-8 text-sm">
          {page.sections.map((s) => (
            <section key={s.id} id={s.id} className="scroll-mt-8">
              <h2 className="text-base font-semibold text-white">
                {s.title}
              </h2>
              <div className="mt-1">
                <Body body={s.body} />
              </div>
            </section>
          ))}
        </div>
      </main>
      <MarketingFooter />
    </div>
  )
}

// Re-export for any callers that want to iterate over legal pages outside
// the route file (e.g. sitemap generation later).
export { allLegalPages }
